/* @flow */
import * as _ from "lodash";

import { Bundle } from "shared/src/types/articlesearch/Article";

export function filter(
    bundles: {
        [x: string]: Bundle;
    },
    availabilityType: string
): Bundle[] {
    return _.filter(bundles, { availability: availabilityType });
}

// Sort map by keys.
export function sortBundlesAscending(bundleAsArray: Bundle[]): Bundle[] {
    return _.sortBy(bundleAsArray, (bundle) => bundle.bettyBundleId.bundleNumber);
}

export function filterSearchableBundles(
    bundles: {
        [x: string]: Bundle;
    },
    storeId: string | undefined | null
): {
    [x: string]: Bundle;
} {
    return _.pickBy(bundles, (bundle) => (storeId ? _.get(bundle, ["stores", storeId, "customerSearchable"]) : null));
}

export function filterBuyableBundles(
    bundles: {
        [x: string]: Bundle;
    },
    storeId: string | undefined | null
): {
    [x: string]: Bundle;
} {
    return _.pickBy(bundles, (bundle) => (storeId ? _.get(bundle, ["stores", storeId, "customerBuyable"]) : null));
}

// Gets a map from BundleNumber to BundleInformation
export function choosePreselectedBundleRegardingAvailability(bundles: { [x: string]: Bundle }): Bundle {
    const numberOfBundles = _.size(bundles);
    // We do not need to filter if there is only one bundle.
    if (numberOfBundles < 2) {
        return bundles[_.keys(bundles)[0]];
    }
    // First priority: Get a available bundle if one is there
    let filterOutAvailableBundles = filter(bundles, "AVAILABLE");
    if (filterOutAvailableBundles.length > 0) {
        filterOutAvailableBundles = sortBundlesAscending(filterOutAvailableBundles);
        return filterOutAvailableBundles[0];
    }
    // Second priority: Get a limited bundle if one is there
    let filterOutLimitedBundles = filter(bundles, "LIMITED");
    if (filterOutLimitedBundles.length > 0) {
        filterOutLimitedBundles = sortBundlesAscending(filterOutLimitedBundles);
        return filterOutLimitedBundles[0];
    }
    // Third priority: Get a unavailable bundle if one is there
    let filterOutUnavailableBundles = filter(bundles, "UNAVAILABLE");
    if (filterOutUnavailableBundles.length > 0) {
        filterOutUnavailableBundles = sortBundlesAscending(filterOutUnavailableBundles);
        return filterOutUnavailableBundles[0];
    }
    return bundles[_.keys(bundles)[0]];
}

export function chooseBundleToShow(
    bundles: {
        [x: string]: Bundle;
    },
    storeId: string | undefined | null,
    listedItemId?: string | null,
    query?: string | undefined | null
): string {
    return getBundleToShow(bundles, storeId, listedItemId, query).bettyBundleId.bettyBundleId;
}

export function getBundleToShow(
    bundles: {
        [x: string]: Bundle;
    },
    storeId: string | undefined | null,
    listedItemId?: string | null,
    query?: string | undefined | null
): Bundle {
    const directBundleHitFromSearchQuery = getBundleFromQuery(bundles, query);
    if (directBundleHitFromSearchQuery) {
        return directBundleHitFromSearchQuery;
    }

    const filteredSearchableBundles = filterSearchableBundles(bundles, storeId);
    if (listedItemId && filteredSearchableBundles[listedItemId]) {
        return filteredSearchableBundles[listedItemId];
    }

    const filteredSearchableAndBuyableBundles = filterBuyableBundles(filteredSearchableBundles, storeId);
    if (!_.isEmpty(filteredSearchableAndBuyableBundles)) {
        return choosePreselectedBundleRegardingAvailability(filteredSearchableAndBuyableBundles);
    } else if (!_.isEmpty(filteredSearchableBundles)) {
        return choosePreselectedBundleRegardingAvailability(filteredSearchableBundles);
    } else if (listedItemId) {
        if (bundles[listedItemId]) {
            const prechoseBundleData = bundles[listedItemId];
            if (prechoseBundleData.availability === "AVAILABLE" || prechoseBundleData.availability === "LIMITED") {
                return prechoseBundleData;
            }
        }
    }
    return choosePreselectedBundleRegardingAvailability(bundles);
}

export function getBundleFromQuery(
    bundles: {
        [x: string]: Bundle;
    },
    query: string | undefined | null
) {
    const queryWithoutLeadingZeros = _.trimStart(query || "", "0");
    if (!query || !queryWithoutLeadingZeros || _.size(bundles) <= 1) {
        return null;
    }
    const bundlesWithQueryIdMatch = _.filter(bundles, (bundle) => {
        const referencedArticles = _.get(bundle, ["referencedArticles"]);
        // Leading articles have more than one referenced article, but this logic doesn't apply for leading articles
        return (
            _.size(referencedArticles) === 1 &&
            (queryWithoutLeadingZeros === (_.head(referencedArticles) as any).subsystemArticleNumber ||
                queryWithoutLeadingZeros === (_.head(referencedArticles) as any).mgbBundleNumber)
        );
    });
    // There should only be one direct match. If there are more, something is probably off and the other selection
    // logic should be used
    return _.size(bundlesWithQueryIdMatch) === 1 ? bundlesWithQueryIdMatch[0] : null;
}
